import React, { useState } from "react";
import { ArrowRight } from "lucide-react";
import toast from "react-hot-toast";
import FormField from "../ui/FormField";
import Typography from "../ui/Typography";

interface ScheduleConsultationProps {
  onSuccess: () => void;
}

const ScheduleConsultation = ({ onSuccess }: ScheduleConsultationProps) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (submitStatus !== "idle") {
      setSubmitStatus("idle");
    }
  };

  const validateForm = () => {
    const requiredFields: (keyof typeof formData)[] = [
      "firstName",
      "lastName",
      "email",
      "message",
    ];

    return requiredFields.every(
      (field) => formData[field].trim() !== ""
    );
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSubmitting) return;

    if (!validateForm()) {
      setSubmitStatus("error");
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);

    try {
      const accessKey = import.meta.env.VITE_WEB3FORMS_ACCESS_KEY;
      if (!accessKey) {
        toast.error("Form configuration error. Please contact us directly.");
        setIsSubmitting(false);
        return;
      }
      const payload = new FormData(event.currentTarget);
      payload.append("access_key", accessKey);

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: payload,
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setSubmitStatus("success");
        toast.success("Form submitted successfully!");
        onSuccess();
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          company: "",
          phone: "",
          message: "",
        });
      } else {
        setSubmitStatus("error");
        toast.error(
          'Form submission failed. Kindly check the mail if you included "@".'
        );
      }
    } catch (_error) {
      setSubmitStatus("error");
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="schedule">
      <div className="border-gradient w-full max-w-3xl mx-auto">
        <div className="p-8 md:p-10">
          {/* Header — seamless, no border separator */}
          <div className="text-center mb-8">
            <Typography.Title className="mb-2">
              Ready for accelerated growth?
            </Typography.Title>
            <Typography.Caption>
              Free 30-minute consultation. No commitment.
            </Typography.Caption>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            {/* Name row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                id="sc-firstName"
                label="First name"
                name="firstName"
                value={formData.firstName}
                onChange={(value) => handleInputChange("firstName", value)}
                placeholder="John"
                required
              />
              <FormField
                id="sc-lastName"
                label="Last name"
                name="lastName"
                value={formData.lastName}
                onChange={(value) => handleInputChange("lastName", value)}
                placeholder="Doe"
                required
              />
            </div>

            {/* Email — full width, most important field */}
            <FormField
              id="sc-email"
              label="Work Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={(value) => handleInputChange("email", value)}
              placeholder="john@company.com"
              required
            />

            {/* Optional fields — same row, less visual weight */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                id="sc-company"
                label="Company (optional)"
                name="company"
                value={formData.company}
                onChange={(value) => handleInputChange("company", value)}
                placeholder="Acme Corp"
              />
              <FormField
                id="sc-phone"
                label="Phone (optional)"
                name="phone"
                type="tel"
                inputMode="numeric"
                value={formData.phone}
                onChange={(value) => handleInputChange("phone", value.replace(/[^0-9]/g, ""))}
                placeholder="1234567890"
              />
            </div>

            {/* Message — more inviting with extra rows */}
            <FormField
              id="sc-message"
              label="Tell us about your project"
              name="message"
              value={formData.message}
              onChange={(value) => handleInputChange("message", value)}
              placeholder="What challenges are you facing? What are you looking to build?"
              multiline
              rows={5}
              required
            />

            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`group w-full px-6 py-4 rounded-md text-btn transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer focus-visible:ring-2 focus-visible:ring-indigo-500/50 focus-visible:outline-none ${
                submitStatus === "success"
                  ? "bg-green-600 text-white"
                  : submitStatus === "error"
                  ? "bg-red-500/90 text-white"
                  : isSubmitting
                  ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                  : "bg-white text-black hover:bg-gray-100 hover:shadow-lg hover:shadow-white/10"
              }`}
            >
              {submitStatus === "success"
                ? "Submitted!"
                : submitStatus === "error"
                ? "Please fill required fields"
                : isSubmitting
                ? "Sending..."
                : (
                  <>
                    Book Free Consultation
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ScheduleConsultation;
